import { Text } from "../../../../atoms/Text";
import { formatCurrencyGBPPounds } from "../../../../lib/currency";
import { formatQuantity } from "../../../../lib/quantity";
import { hideBelowClass } from "../collapse";
import { Money } from "../primitives";
import { LEAD_WIDTH, type LedgerCollapse } from "../types";

export interface ItemSubtotalStripProps {
  collapse: LedgerCollapse;
  /**
   * Whole percent of the item's target that is booked. Null means the figure
   * is withheld from this viewer, not that it is zero — the same rule `Money`
   * follows, and a viewer without financial permission has none of the three.
   */
  percentBooked: number | null;
  /** What is left of the target. Null when withheld. */
  remaining: number | null;
  /** Sum of the peer rows above. Null when withheld. */
  bookedTotal: number | null;
  /** Quantity left for unit-priced items. */
  remainingQuantity?: { value: number; unitLabel: string } | null;
  showValues?: boolean;
}

/** Same stand-in `Money` uses, so a redacted strip reads like a redacted row. */
const REDACTED = "··";

/**
 * Caps a run of peer booking rows with booked progress and what remains.
 * Unit-priced items show both quantity and value; fixed items show value only.
 */
export const ItemSubtotalStrip = ({
  collapse,
  percentBooked,
  remaining,
  bookedTotal,
  remainingQuantity,
  showValues = true,
}: ItemSubtotalStripProps) => (
  <div className="flex items-center gap-2 border-b border-border-default px-4 py-1">
    <div className="shrink-0" style={{ width: LEAD_WIDTH }} />
    <Text
      as="div"
      variant="inherit"
      color="secondary"
      className="truncate text-(length:--ledger-text-cell) leading-[1.66] tabular-nums"
    >
      {percentBooked == null ? REDACTED : `${percentBooked}%`} booked
      {showValues && (
        <span className={hideBelowClass(collapse)}>
          {remainingQuantity ? (
            <>
              {" "}
              · Remaining:{" "}
              {formatQuantity(
                remainingQuantity.value,
                remainingQuantity.unitLabel,
              )}{" "}
              ·{" "}
              {remaining == null
                ? REDACTED
                : formatCurrencyGBPPounds(remaining)}
            </>
          ) : (
            <>
              {" "}
              ·{" "}
              {remaining == null
                ? REDACTED
                : formatCurrencyGBPPounds(remaining)}{" "}
              remaining
            </>
          )}
        </span>
      )}
    </Text>
    <div className="flex-1" />
    {showValues && (
      <div className="w-(--ledger-value-width) shrink-0 text-right">
        <Money value={bookedTotal} />
      </div>
    )}
  </div>
);
